import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from 'components/Button';
import { type MultipleChoiceAnswer } from 'shared/modelTypes/game';
import type { TaskAnswerMultipleChoice, TaskAnswerSingleChoice } from 'shared/modelTypes/client';
import { ShieldAlert } from 'lucide-react';
import { useStore } from 'engine/store';

const multipleChoiceQuestionSchema = z.object({
	answer: z.string().min(1, 'Answer is required'),
});

type MultipleChoiceQuestionFormData = z.infer<typeof multipleChoiceQuestionSchema>;

export const MultipleChoiceQuestionForm = ({
	answer,
	readOnly,
	onSubmit,
	options,
}: {
	answer?: TaskAnswerSingleChoice;
	readOnly: boolean;
	onSubmit: (answer: TaskAnswerSingleChoice) => void;
	options: MultipleChoiceAnswer[];
}) => {
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<MultipleChoiceQuestionFormData>({
		resolver: zodResolver(multipleChoiceQuestionSchema),
		defaultValues: {
			answer: answer?.chosenId ?? '',
		},
	});

	const state = useStore.getState();
	const isFrozen = useStore((s) => s.isFrozen);
	const isCaptain: boolean = state.teams.find((t) => t.id === state.teamId)?.captainId == state.deviceId;

	return (
		<form
			onSubmit={handleSubmit((data) => {
				if (!readOnly) {
					// if (typeof data.answer !== 'string') data.answer = data.answer.join(',');
					onSubmit({ kind: 'single-choice', chosenId: data.answer });
				}
			})}
			className="flex flex-col gap-4"
		>
			<div>
				<label htmlFor="answer" className="--skin-text block text-sm font-medium opacity-90">
					{__`Answer`}
				</label>
				<fieldset>
					<ul className="flex w-fit min-w-full flex-col gap-2">
						{options.map((option) => (
							<li key={option.id}>
								<div className="flex items-center rounded border skin-border-button">
									<input
										className="peer hidden appearance-none"
										type={'radio'}
										id={'radio-' + option.id}
										disabled={readOnly}
										readOnly={readOnly}
										value={option.id}
										defaultChecked={answer?.chosenId === option.id}
										{...register('answer')}
									/>
									<label
										className={`w-full p-4 peer-checked:bg-[var(--skin-text)] peer-checked:text-[var(--skin-bg)] ${answer?.chosenId === option.id ? 'bg-[var(--skin-text)] text-[var(--skin-bg)]' : ''}`}
										htmlFor={'radio-' + option.id}
									>
										{option.name}
									</label>
								</div>
							</li>
						))}
					</ul>
				</fieldset>

				{errors.answer && <p className="mt-1 text-sm text-red-600">{errors.answer.message}</p>}
			</div>
			{!answer && readOnly && (
				<div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/40">
					<div className="flex w-full flex-col items-center bg-red-500/80 py-2 text-white shadow-lg">
						<ShieldAlert className="mb-1 size-6" />
						<p className="text-sm font-medium">
							{isFrozen
								? __`Game has been frozen by the game master. Submitting answers currently not available.`
								: isCaptain
									? __`Submitting answers not available.`
									: __`Submitting answers not available in spectator mode.`}
						</p>
					</div>
				</div>
			)}
			{!readOnly && (
				<Button className="w-full" type="submit">
					{__`Submit`}
				</Button>
			)}
		</form>
	);
};

const multiSelectMultipleChoiceQuestionSchema = z.object({
	answer: z.array(z.string()),
});

type MultiSelectMultipleChoiceQuestionFormData = z.infer<typeof multiSelectMultipleChoiceQuestionSchema>;

export const MultiSelectMultipleChoiceQuestionForm = ({
	answer,
	readOnly,
	onSubmit,
	options,
}: {
	answer?: TaskAnswerMultipleChoice;
	readOnly: boolean;
	onSubmit: (answer: TaskAnswerMultipleChoice) => void;
	options: MultipleChoiceAnswer[];
}) => {
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<MultiSelectMultipleChoiceQuestionFormData>({
		resolver: zodResolver(multiSelectMultipleChoiceQuestionSchema),
		defaultValues: {
			answer: answer?.chosenIds ?? [],
		},
	});
	return (
		<form
			onSubmit={handleSubmit((data) => {
				if (!readOnly) {
					// if (typeof data.answer !== 'string') data.answer = data.answer.join(',');
					onSubmit({ kind: 'multiple-choice', chosenIds: data.answer });
				}
			})}
			className="flex flex-col gap-4"
		>
			<div>
				<label htmlFor="answer" className="--skin-text block text-sm font-medium opacity-90">
					Answer {'(multiple answers are possible)'}
				</label>
				<fieldset>
					<ul className="flex w-fit min-w-full flex-col gap-2">
						{options.map((option) => (
							<li key={option.id}>
								<div className="flex items-center rounded border skin-border-button">
									<input
										className="peer appearance-none"
										type={'checkbox'}
										id={'checkbox-' + option.id}
										disabled={readOnly}
										readOnly={readOnly}
										value={option.id}
										defaultChecked={answer?.chosenIds.includes(option.id) || undefined}
										{...register('answer')}
									/>
									<label
										className="w-full p-4 peer-checked:bg-[var(--skin-text)] peer-checked:text-[var(--skin-bg)]"
										htmlFor={'checkbox-' + option.id}
									>
										{option.name}
									</label>
								</div>
							</li>
						))}
					</ul>
				</fieldset>

				{errors.answer && <p className="mt-1 text-sm text-red-600">{errors.answer.message}</p>}
			</div>
			{!readOnly && (
				<Button className="w-full" type="submit">
					{__`Submit`}
				</Button>
			)}
		</form>
	);
};
